import React, { useState, useEffect } from 'react';

const API = 'https://talku.online';

const PROVIDERS = [
  {
    id: 'anthropic', name: 'Anthropic', url: 'console.anthropic.com',
    desc: 'Claude — razonamiento avanzado, comprensión de contexto y herramientas para agentes conversacionales.',
    keyPlaceholder: 'sk-ant-api03-...',
    keyHint: 'Encuéntrala en console.anthropic.com → API Keys',
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="6" fill="#0a0a0a"/><path transform="translate(5 5) scale(0.5833)" d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z" fill="#fff"/></svg>,
  },
  {
    id: 'openai', name: 'OpenAI', url: 'platform.openai.com',
    desc: 'GPT — modelos de lenguaje versátiles con capacidades de razonamiento y generación de texto.',
    keyPlaceholder: 'sk-proj-...',
    keyHint: 'Encuéntrala en platform.openai.com → API Keys',
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="6" fill="#0a0a0a"/><path transform="translate(5 5) scale(0.5833)" d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" fill="#fff"/></svg>,
  },
];

// Plantillas de partida para tenants nuevos (≠1): sin datos de ningún negocio.
// Los [[campos]] son obligatorios: no se puede avanzar ni guardar hasta reemplazarlos.
const STARTER_TEMPLATES = [
  {
    id: 'ventas', label: 'Ventas', desc: 'Asesora comercial que responde siempre y lleva la conversación a la venta',
    prompt: `Eres [[Nombre de la asesora]], asesora de ventas y atención al cliente de [[Nombre del negocio]]. Representas al negocio como una persona real del equipo; nunca digas que eres un bot o una inteligencia artificial.

Vendemos [[Qué vendes]]. Para productos, precios y disponibilidad usa solo la información de la base de conocimiento; si no tienes un dato, dilo con naturalidad y ofrece confirmarlo con un asesor. Nunca inventes precios, características, descuentos ni promociones.

Tu objetivo es ayudar al cliente y, cuando haya interés, llevar la conversación con naturalidad hacia la compra. No eres un contestador de preguntas: entiendes el contexto, recomiendas, resuelves dudas y propones el siguiente paso. Piensa siempre en la conversación completa y no vuelvas a preguntar lo que el cliente ya dijo.

Vende como alguien que ayuda a elegir, no como un catálogo: si hay varias opciones, recomienda una. Cuando el cliente muestre intención de compra, avanza con decisiones concretas ("¿te lo enviamos a casa o al trabajo?") en lugar de "¿deseas comprar?". Si algo frena la decisión (precio, confianza, talla, envío o forma de pago), resuélvelo primero. No inventes urgencia ni escasez.

Los envíos tardan de 2 a 5 días hábiles según la ciudad. Recibimos pago contra entrega y transferencia. La garantía es de 30 días después de recibir el producto, siempre que no esté usado; los fletes del cambio los asume el cliente.

Cuando el cliente decida comprar, confirma producto, color y talla, y pide de forma natural (no como formulario) su nombre completo, ciudad, departamento, barrio y dirección completa. Con los datos completos, resume el pedido en una línea y pasa la conversación a un asesor para que lo confirme. Si el cliente habla de garantía, cambio o devolución, pásalo también a un asesor.

Escribe como una asesora colombiana real: mensajes cortos (1 a 3 líneas), una pregunta a la vez, cercanos y sin lenguaje corporativo. Máximo un emoji cuando aporte. Si el cliente solo saluda, preséntate y pregúntale qué está buscando.

En cada respuesta piensa: qué quiere realmente el cliente, qué sé ya por la conversación y cuál es el siguiente paso natural para ayudarlo o acercarlo a la compra.`,
  },
  {
    id: 'citas', label: 'Agendar citas o llamadas', desc: 'Atiende interesados y coordina una cita o llamada',
    prompt: `Eres [[Nombre de la asesora]], del equipo de [[Nombre del negocio]]. Tu tarea es atender a las personas interesadas y agendarles [[Qué se agenda (citas, llamadas, demostraciones)]].

Explica brevemente lo que hacemos cuando te pregunten y usa la base de conocimiento para resolver dudas; si no tienes un dato, no lo inventes y ofrece que un asesor lo confirme.

Atendemos de lunes a viernes de 8 a. m. a 6 p. m.; cada cita dura unos 30 minutos. Para agendar pide, en un solo mensaje y de forma natural, el nombre, el teléfono y el motivo, y confirma el día y la hora acordados. Si no hay disponibilidad clara, pasa la conversación a un asesor.

Escribe mensajes cortos y amables, una pregunta a la vez, sin lenguaje corporativo.`,
  },
  {
    id: 'soporte', label: 'Atención al cliente', desc: 'Resuelve preguntas frecuentes y escala a un asesor cuando hace falta',
    prompt: `Eres [[Nombre de la asesora]], del equipo de atención al cliente de [[Nombre del negocio]]. Ayudas a los clientes a resolver sus dudas de forma rápida y amable.

Usa la base de conocimiento para responder; nunca inventes información. Si la pregunta no está cubierta, o el cliente está molesto, tiene un reclamo o pide hablar con una persona, pasa la conversación a un asesor.

Atendemos de lunes a sábado de 8 a. m. a 6 p. m. Escribe mensajes cortos y claros, una pregunta a la vez, con tono cercano y sin lenguaje corporativo.`,
  },
  { id: 'blanco', label: 'En blanco', desc: 'Escribe las instrucciones desde cero', prompt: '' },
];

const pendingFields = (text) => [...new Set((String(text || '').match(/\[\[[^\]]+\]\]/g) || []).map(m => m.slice(2, -2)))];

const tokenTenantId = (token) => {
  try { return Number(JSON.parse(atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).tenant_id) || 1; }
  catch { return 1; }
};

function AILabInner({ user }) {
  const authH = () => ({ 'Authorization': `Bearer ${user?.token || localStorage.getItem('talku_token') || ''}`, 'Content-Type': 'application/json' });
  // Solo decide qué se muestra; el aislamiento real lo hace el backend
  const isNewTenant = tokenTenantId(user?.token || localStorage.getItem('talku_token')) !== 1;

  const [configs, setConfigs] = useState({});
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [detail, setDetail] = useState(null);

  const [apiKey, setApiKey] = useState('');
  const [defModel, setDefModel] = useState('');
  const [agents, setAgents] = useState([]);
  const [testing, setTesting] = useState(false);
  const [testStatus, setTestStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [cStep, setCStep] = useState(0);
  const [cTrigger, setCTrigger] = useState('');
  const [cName, setCName] = useState('');
  const [cDesc, setCDesc] = useState('');
  const [cPrompt, setCPrompt] = useState('');
  const [cTools, setCTools] = useState([]);
  const [cModel, setCModel] = useState('');
  const [cTemplate, setCTemplate] = useState('');

  const [editAgent, setEditAgent] = useState(null);

  const [playAgent, setPlayAgent] = useState(null);
  const [playMsg, setPlayMsg] = useState('');
  const [playChat, setPlayChat] = useState([]); // [{ role: 'user'|'assistant', content, action? }]

  const [kbDocs, setKbDocs] = useState([]);
  const [kbEdit, setKbEdit] = useState(null);
  const [kbSaving, setKbSaving] = useState(false);
  const [playLoading, setPlayLoading] = useState(false);

  useEffect(() => { loadAll(); }, []);
  // Una sola página: se abre el proveedor conectado (o OpenAI) al cargar
  useEffect(() => {
    if (loading || detail) return;
    const connected = PROVIDERS.find(p => configs[p.id]);
    openDetail((connected || PROVIDERS.find(p => p.id === 'openai')).id);
  }, [loading]); // eslint-disable-line

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cfgR, metaR, kbR] = await Promise.all([
        fetch(`${API}/ai/configs`, { headers: authH() }),
        fetch(`${API}/ai/metadata`, { headers: authH() }),
        fetch(`${API}/knowledge/`, { headers: authH() }),
      ]);
      setSessionExpired(cfgR.status === 401 || cfgR.status === 403);
      const cf = await cfgR.json().catch(() => ({}));
      setConfigs(cf && typeof cf.configs === 'object' && cf.configs ? cf.configs : {});
      setMeta(await metaR.json());
      const kb = await kbR.json().catch(() => []);
      setKbDocs(Array.isArray(kb) ? kb : (Array.isArray(kb?.documents) ? kb.documents : []));
    } catch {}
    setLoading(false);
  };

  const saveKbDoc = async () => {
    if (!kbEdit || !kbEdit.title.trim() || !kbEdit.content.trim()) return;
    setKbSaving(true);
    try {
      if (kbEdit.id) {
        const r = await fetch(`${API}/knowledge/${kbEdit.id}`, { method: 'PUT', headers: authH(), body: JSON.stringify({ title: kbEdit.title, content: kbEdit.content, category: kbEdit.category || 'general' }) });
        if (r.ok) { setKbDocs(prev => prev.map(d => d.id === kbEdit.id ? { ...d, title: kbEdit.title, content: kbEdit.content, category: kbEdit.category } : d)); setKbEdit(null); }
      } else {
        const r = await fetch(`${API}/knowledge/`, { method: 'POST', headers: authH(), body: JSON.stringify({ title: kbEdit.title, content: kbEdit.content, category: kbEdit.category || 'general' }) });
        if (r.ok) { const d = await r.json(); setKbDocs(prev => [...prev, { ...d, content: kbEdit.content, active: true }]); setKbEdit(null); }
      }
    } catch {}
    setKbSaving(false);
  };

  const deleteKbDoc = async (id) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      const r = await fetch(`${API}/knowledge/${id}`, { method: 'DELETE', headers: authH() });
      if (r.ok) setKbDocs(prev => prev.filter(d => d.id !== id));
    } catch {}
  };

  const toggleKbDoc = async (doc) => {
    try {
      const r = await fetch(`${API}/knowledge/${doc.id}`, { method: 'PUT', headers: authH(), body: JSON.stringify({ active: !doc.active }) });
      if (r.ok) setKbDocs(prev => prev.map(d => d.id === doc.id ? { ...d, active: !d.active } : d));
    } catch {}
  };

  const openDetail = async (pid) => {
    setDetail(pid); setApiKey(''); setTestStatus(null);
    setDefModel(meta?.defaultModels?.[pid] || '');
    try {
      const r = await fetch(`${API}/ai/agents/${pid}`, { headers: authH() });
      setAgents((await r.json()).agents || []);
    } catch { setAgents([]); }
  };

  const saveConfig = async () => {
    if (!apiKey.trim() || !detail) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/ai/config/${detail}`, {
        method: 'POST', headers: authH(),
        body: JSON.stringify({ api_key: apiKey.trim(), default_model: defModel }),
      });
      if (r.ok) { const d = await r.json(); setConfigs(prev => ({ ...prev, [detail]: d.config })); setApiKey(''); setTestStatus(null); }
    } catch {}
    setSaving(false);
  };

  const toggleConfig = async (pid) => {
    try {
      const r = await fetch(`${API}/ai/config/${pid}/toggle`, { method: 'PUT', headers: authH() });
      if (r.ok) { const d = await r.json(); setConfigs(prev => ({ ...prev, [pid]: d.config })); }
    } catch {}
  };

  const testConfig = async () => {
    if (!detail) return;
    setTesting(true); setTestStatus(null);
    try {
      const r = await fetch(`${API}/ai/config/${detail}/test`, {
        method: 'POST', headers: authH(),
        body: JSON.stringify({ api_key: apiKey.trim(), default_model: defModel }),
      });
      setTestStatus(await r.json());
    } catch { setTestStatus({ ok: false, error: 'Error de conexión' }); }
    setTesting(false);
  };

  const resetCreate = () => { setShowCreate(false); setCStep(0); setCTrigger(''); setCName(''); setCDesc(''); setCPrompt(''); setCTools([]); setCModel(''); setCTemplate(''); };

  const createAgent = async () => {
    if (!detail) return;
    const r = await fetch(`${API}/ai/agents/${detail}`, {
      method: 'POST', headers: authH(),
      body: JSON.stringify({ name: cName, provider: detail, description: cDesc, trigger_type: cTrigger, system_prompt: cPrompt, tools_enabled: cTools, model_override: cModel || null, priority: agents.length + 1 }),
    });
    if (r.ok) { const d = await r.json(); setAgents(prev => [...prev, d.agent]); resetCreate(); }
  };

  const toggleAgent = async (id, active) => {
    const r = await fetch(`${API}/ai/agents/detail/${id}`, { method: 'PUT', headers: authH(), body: JSON.stringify({ active: !active }) });
    if (r.ok) { const d = await r.json(); setAgents(prev => prev.map(a => a.id === id ? d.agent : a)); }
  };

  const deleteAgent = async (id) => {
    if (!window.confirm('¿Eliminar este agente?')) return;
    const r = await fetch(`${API}/ai/agents/detail/${id}`, { method: 'DELETE', headers: authH() });
    if (r.ok) setAgents(prev => prev.filter(a => a.id !== id));
  };

  const saveEdit = async () => {
    if (!editAgent) return;
    if (isNewTenant && pendingFields(editAgent.systemPrompt).length) return;
    const r = await fetch(`${API}/ai/agents/detail/${editAgent.id}`, {
      method: 'PUT', headers: authH(),
      body: JSON.stringify({ name: editAgent.name, description: editAgent.description, trigger_type: editAgent.triggerType, system_prompt: editAgent.systemPrompt, tools_enabled: editAgent.toolsEnabled, model_override: editAgent.modelOverride || null }),
    });
    if (r.ok) { const d = await r.json(); setAgents(prev => prev.map(a => a.id === d.agent.id ? d.agent : a)); setEditAgent(null); }
  };

  const testAgentChat = async (agentId) => {
    const msg = playMsg.trim();
    if (!msg) return;
    const history = playChat.filter(m => !m.error).map(m => ({ role: m.role, content: m.content }));
    setPlayChat(prev => [...prev, { role: 'user', content: msg }]); setPlayMsg('');
    setPlayLoading(true);
    let reply;
    try {
      const r = await fetch(`${API}/ai/agents/detail/${agentId}/test`, { method: 'POST', headers: authH({ 'Content-Type': 'application/json' }), body: JSON.stringify({ message: msg, history }) });
      const d = await r.json();
      reply = d.ok ? { role: 'assistant', content: d.response, action: d.action } : { role: 'assistant', error: true, content: `Error: ${d.error || d.detail || 'no se pudo probar'}` };
    } catch { reply = { role: 'assistant', error: true, content: 'Error de conexión' }; }
    setPlayChat(prev => [...prev, reply]);
    setPlayLoading(false);
  };

  const triggerOpts = meta?.triggers || [];
  const toolOpts = meta?.tools || [];
  const modelOpts = meta?.models || {};
  const promptTemplates = meta?.promptTemplates || {};
  const providerModels = detail ? (modelOpts[detail] || []) : [];
  const prov = PROVIDERS.find(p => p.id === detail);
  const provConfig = detail ? configs[detail] : null;

  if (loading || !prov) return <div className="ai-loading">Cargando…</div>;

  return (
    <div className="ai-page">
      <style>{`
        .ai-page { padding:36px 44px 60px; max-width:1100px; }
        .ai-loading { display:flex; align-items:center; justify-content:center; height:100%; color:#9ca3af; }
        @keyframes aiIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }
        .ai-h1 { font-size:22px; font-weight:600; color:#111; letter-spacing:-.3px; }
        .ai-sub { font-size:13px; color:#888; margin-top:4px; }
        .ai-card { border:1px solid #e5e5e5; border-radius:10px; background:#fff; margin-top:20px; animation:aiIn .3s ease both; }
        .ai-ch { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:16px 20px; border-bottom:1px solid #f0f0f0; flex-wrap:wrap; }
        .ai-ct { display:flex; align-items:center; gap:10px; font-size:15px; font-weight:600; color:#111; }
        .ai-n { width:22px; height:22px; border-radius:6px; background:#111; color:#fff; font-size:12px; font-weight:500; display:flex; align-items:center; justify-content:center; }
        .ai-cs { font-size:12.5px; color:#888; margin-top:2px; font-weight:400; }
        .ai-cb { padding:18px 20px; }
        .ai-seg { display:inline-flex; border:1px solid #e3e3e3; border-radius:8px; overflow:hidden; }
        .ai-seg button { display:flex; align-items:center; gap:8px; border:none; background:#fff; padding:8px 14px; font-family:inherit; font-size:13px; font-weight:400; color:#555; cursor:pointer; transition:background .15s, color .15s; }
        .ai-seg button + button { border-left:1px solid #e3e3e3; }
        .ai-seg button.on { background:#111; color:#fff; }
        .ai-seg svg { width:18px; height:18px; }
        .ai-status { display:inline-flex; align-items:center; gap:7px; font-size:12.5px; color:#555; }
        .ai-dot { width:8px; height:8px; border-radius:2px; background:#c4c4c4; }
        .ai-dot.ok { background:#16a34a; } .ai-dot.off { background:#d97706; }
        .ai-row { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; }
        .ai-lbl { font-size:12px; font-weight:500; color:#444; margin-bottom:6px; }
        .ai-in { width:100%; box-sizing:border-box; border:1px solid #dcdcdc; border-radius:6px; padding:9px 11px; font-size:13px; font-family:inherit; outline:none; transition:border-color .15s; }
        .ai-in:focus { border-color:#111; }
        .ai-hint { font-size:11.5px; color:#999; margin-top:5px; }
        .ai-btn { height:38px; padding:0 14px; border-radius:6px; font-family:inherit; font-size:13px; font-weight:400; cursor:pointer; display:inline-flex; align-items:center; gap:7px; transition:background .15s, border-color .15s; white-space:nowrap; }
        .ai-btn.pri { border:none; background:#111; color:#fff; } .ai-btn.pri:hover:not(:disabled) { background:#2b2b2b; }
        .ai-btn.ghost { border:1px solid #dcdcdc; background:#fff; color:#111; } .ai-btn.ghost:hover:not(:disabled) { background:#f5f5f5; }
        .ai-btn.sm { height:32px; padding:0 11px; font-size:12.5px; }
        .ai-btn.danger { border:1px solid #f1c4c0; background:#fff; color:#b42318; } .ai-btn.danger:hover { background:#fff5f4; }
        .ai-btn:disabled { opacity:.45; cursor:not-allowed; }
        .ai-msg { margin-top:12px; padding:9px 12px; border-radius:6px; font-size:12.5px; animation:aiIn .2s ease both; }
        .ai-msg.ok { background:#effaf4; border:1px solid #c9ead8; color:#1f5f3f; } .ai-msg.err { background:#fff5f4; border:1px solid #f5d0cc; color:#b42318; }
        .ai-models { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:8px; margin-top:16px; }
        .ai-model { text-align:left; border:1px solid #e3e3e3; border-radius:8px; background:#fff; padding:11px 13px; cursor:pointer; font-family:inherit; transition:border-color .15s; }
        .ai-model:hover { border-color:#bdbdbd; } .ai-model.on { border-color:#111; box-shadow:0 0 0 1px #111; }
        .ai-model b { display:block; font-size:13px; font-weight:500; color:#111; } .ai-model span { display:block; font-size:12px; color:#888; margin-top:2px; } .ai-model i { display:block; font-style:normal; font-size:11.5px; color:#999; margin-top:4px; }
        .ai-list { display:flex; flex-direction:column; }
        .ai-item { display:flex; align-items:center; gap:12px; padding:13px 20px; border-top:1px solid #f2f2f2; transition:background .12s; }
        .ai-item:first-child { border-top:none; } .ai-item:hover { background:#fafafa; }
        .ai-iname { font-size:13.5px; font-weight:500; color:#111; } .ai-imeta { font-size:12px; color:#888; margin-top:2px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .ai-tag { font-size:11px; border:1px solid #e3e3e3; border-radius:4px; padding:1px 7px; color:#555; }
        .ai-tog { position:relative; width:38px; height:22px; border-radius:11px; border:none; background:#d9d9d9; cursor:pointer; transition:background .2s; flex-shrink:0; }
        .ai-tog span { position:absolute; top:2px; left:2px; width:18px; height:18px; border-radius:50%; background:#fff; box-shadow:0 1px 2px rgba(0,0,0,.2); transition:left .2s; }
        .ai-tog.on { background:#16a34a; } .ai-tog.on span { left:18px; }
        .ai-empty { padding:30px 20px; text-align:center; font-size:13px; color:#888; }
        .ai-empty b { display:block; font-size:14px; font-weight:500; color:#111; margin-bottom:4px; }
        .ai-page .sett-modal { border-radius:10px; border:1px solid #e5e5e5; }
        @keyframes aiPop { from { opacity:0; transform:translateY(10px) scale(.985); } to { opacity:1; transform:none; } }
        .ai-modal { border-radius:10px !important; border:1px solid #e5e5e5; box-shadow:0 18px 48px rgba(10,10,10,.16) !important; animation:aiPop .25s cubic-bezier(.22,1,.36,1) both; }
        .ai-modal .sett-input, .ai-modal .sett-select { border-radius:6px !important; border:1px solid #dcdcdc !important; }
        .ai-modal .sett-input:focus { border-color:#111 !important; box-shadow:0 0 0 2px rgba(10,10,10,.06) !important; }
        .ai-modal .sett-btn { border-radius:6px !important; font-weight:400 !important; }
        .ai-modal .sett-tag { border-radius:4px !important; font-weight:400 !important; }
        .ai-steps { display:flex; gap:6px; margin-bottom:22px; }
        .ai-step { flex:1; display:flex; flex-direction:column; gap:6px; font-size:11.5px; color:#aaa; }
        .ai-step span { height:3px; border-radius:2px; background:#e8e8e8; transition:background .2s; }
        .ai-step.on span { background:#111; } .ai-step.on { color:#555; } .ai-step.cur { color:#111; font-weight:500; }
        .ai-page .sett-btn { border-radius:6px; font-weight:400; }
        .ai-page .sett-input, .ai-page .sett-select { border-radius:6px; }
      `}</style>

      <div className="ai-h1">Inteligencia artificial</div>
      <div className="ai-sub">Configura la IA que responde los chats de tu negocio en 3 pasos.</div>
      {sessionExpired && (
        <div className="ai-msg err" style={{marginTop:16}}>Tu sesión expiró, por eso no se ve tu configuración (no se borró nada). Cierra sesión y vuelve a entrar.</div>
      )}

      {/* 1 · Proveedor */}
      <div className="ai-card">
        <div className="ai-ch">
          <div>
            <div className="ai-ct"><span className="ai-n">1</span>Proveedor de IA</div>
            <div className="ai-cs">La clave es de tu propia cuenta: el consumo se cobra allí.</div>
          </div>
          <div className="ai-seg">
            {PROVIDERS.map(p => <button key={p.id} className={detail===p.id?'on':''} onClick={()=>openDetail(p.id)}>{p.icon}{p.name}</button>)}
          </div>
        </div>
        <div className="ai-cb">
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,marginBottom:14,flexWrap:'wrap'}}>
            <span className="ai-status">
              <span className={`ai-dot${provConfig ? (provConfig.active ? ' ok' : ' off') : ''}`}/>
              {provConfig ? (provConfig.active ? `Conectada · ${provConfig.api_key_masked || ''}` : `Pausada · ${provConfig.api_key_masked || ''}`) : 'Sin conectar'}
            </span>
            {provConfig && (
              <span style={{display:'flex',alignItems:'center',gap:8,fontSize:12.5,color:'#555'}}>
                {provConfig.active ? 'Activa' : 'Pausada'}
                <button className={`ai-tog${provConfig.active?' on':''}`} onClick={() => toggleConfig(detail)} aria-label="Activar o desactivar"><span/></button>
              </span>
            )}
          </div>
          <div className="ai-row">
            <div style={{flex:1,minWidth:240}}>
              <div className="ai-lbl">{provConfig ? 'Cambiar API key' : 'API key'}</div>
              <input className="ai-in" type="password" placeholder={prov.keyPlaceholder} value={apiKey} onChange={e => setApiKey(e.target.value)} style={{fontFamily:'monospace'}}/>
              <div className="ai-hint">{prov.keyHint}</div>
            </div>
            <div style={{display:'flex',gap:8,paddingBottom:22}}>
              <button className="ai-btn ghost" onClick={testConfig} disabled={testing || (!apiKey.trim() && !provConfig)}>{testing ? 'Verificando…' : 'Verificar'}</button>
              <button className="ai-btn pri" onClick={saveConfig} disabled={!apiKey.trim() || saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
            </div>
          </div>
          {testStatus && <div className={`ai-msg ${testStatus.ok ? 'ok' : 'err'}`}>{testStatus.ok ? `Conexión correcta · modelo ${testStatus.model}` : `No funcionó: ${testStatus.error}`}</div>}
          {providerModels.length > 0 && (
            <>
              <div className="ai-lbl" style={{marginTop:18}}>Modelo predeterminado</div>
              <div className="ai-models" style={{marginTop:0}}>
                {providerModels.map(m => (
                  <button key={m.id} className={`ai-model${defModel===m.id?' on':''}`} onClick={() => setDefModel(m.id)}>
                    <b>{m.label}</b><span>{m.desc}</span><i>{m.cost}</i>
                  </button>
                ))}
              </div>
              <div className="ai-hint">El modelo se aplica al guardar la API key.</div>
            </>
          )}
        </div>
      </div>

      {/* 2 · Agentes */}
      <div className="ai-card" style={{animationDelay:'60ms'}}>
        <div className="ai-ch">
          <div>
            <div className="ai-ct"><span className="ai-n">2</span>Agentes</div>
            <div className="ai-cs">Quién responde y con qué instrucciones · {agents.length} con {prov.name}</div>
          </div>
          <button className="ai-btn pri" onClick={() => setShowCreate(true)} disabled={!provConfig}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Crear agente
          </button>
        </div>
        {provConfig && !provConfig.active && agents.some(a => a.active) && (
          <div className="ai-msg err" style={{marginBottom:12}}>La conexión con {prov.name} está pausada: tus agentes no les responden a los clientes. Actívala en el paso 1 para que vuelvan a responder.</div>
        )}
        {!provConfig ? (
          <div className="ai-empty"><b>Primero conecta tu API key</b>Guárdala en el paso 1 para poder crear agentes.</div>
        ) : agents.length === 0 ? (
          <div className="ai-empty"><b>Aún no tienes agentes</b>Sin un agente activo el bot no responde: los chats esperan a tu equipo.</div>
        ) : (
          <div className="ai-list">
            {agents.map(a => (
              <div key={a.id} className="ai-item">
                <div style={{flex:1,minWidth:0}}>
                  <div className="ai-iname">{a.name}</div>
                  <div className="ai-imeta">{a.triggerLabel}{a.modelOverride ? ` · ${a.modelOverride}` : ''}{a.description ? ` · ${a.description}` : ''}</div>
                </div>
                <button className="ai-btn ghost sm" onClick={() => { setPlayAgent(a); setPlayMsg(''); setPlayChat([]); }}>Probar</button>
                <button className="ai-btn ghost sm" onClick={() => setEditAgent({...a})}>Editar</button>
                <button className="ai-btn danger sm" onClick={() => deleteAgent(a.id)} aria-label="Eliminar"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg></button>
                <button className={`ai-tog${a.active?' on':''}`} onClick={() => toggleAgent(a.id, a.active)} aria-label="Activar o desactivar"><span/></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 3 · Base de conocimiento */}
      <div className="ai-card" style={{animationDelay:'120ms'}}>
        <div className="ai-ch">
          <div>
            <div className="ai-ct"><span className="ai-n">3</span>Base de conocimiento</div>
            <div className="ai-cs">Información que el bot consulta: catálogo, precios, políticas, preguntas frecuentes · {kbDocs.length} documento{kbDocs.length !== 1 ? 's' : ''}</div>
          </div>
          <button className="ai-btn ghost" onClick={() => setKbEdit({ title: '', content: '', category: 'general' })}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Nuevo documento
          </button>
        </div>
        {kbDocs.length === 0 ? (
          <div className="ai-empty"><b>Sin documentos</b>Opcional, pero mejora mucho las respuestas.</div>
        ) : (
          <div className="ai-list">
            {kbDocs.map(d => (
              <div key={d.id} className="ai-item">
                <div style={{flex:1,minWidth:0}}>
                  <div className="ai-iname">{d.title}</div>
                  <div className="ai-imeta">{d.content?.slice(0, 90)}{(d.content?.length || 0) > 90 ? '…' : ''}</div>
                </div>
                <span className="ai-tag">{d.category}</span>
                <button className="ai-btn ghost sm" onClick={() => setKbEdit({ id: d.id, title: d.title, content: d.content, category: d.category || 'general' })}>Editar</button>
                <button className="ai-btn danger sm" onClick={() => deleteKbDoc(d.id)} aria-label="Eliminar"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg></button>
                <button className={`ai-tog${d.active?' on':''}`} onClick={() => toggleKbDoc(d)} aria-label="Activar o desactivar"><span/></button>
              </div>
            ))}
          </div>
        )}
      </div>

        {/* MODAL: CREAR AGENTE */}
        {showCreate && (
          <div className="sett-modal-overlay" onClick={e => { if (e.target === e.currentTarget) resetCreate(); }}>
            <div className="sett-modal ai-modal" style={{ width: 620, maxHeight: '90vh', overflow: 'auto' }}>
              <div style={{ padding: '24px 28px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>Nuevo agente — {prov.name}</div>
                <button className="sett-btn sett-btn-ghost" style={{ width: 30, height: 30, padding: 0, borderRadius: 6, justifyContent: 'center' }} onClick={resetCreate}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div style={{ padding: '20px 28px 28px' }}>
                <div className="ai-steps">
                  {['Tipo','Perfil','Instrucciones','Herramientas','Resumen'].map((s,i) => (
                    <div key={s} className={`ai-step${cStep>=i?' on':''}${cStep===i?' cur':''}`}><span/>{s}</div>
                  ))}
                </div>

                {cStep === 0 && (
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#0a0a0a', marginBottom: 14 }}>¿Cuándo debe responder este agente?</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {triggerOpts.map(t => (
                        <div key={t.id} onClick={() => { setCTrigger(t.id); if (!isNewTenant && promptTemplates[t.id]) setCPrompt(promptTemplates[t.id]); }}
                          style={{ padding: '14px 18px', borderRadius: 8, cursor: 'pointer', transition: 'all .15s', border: cTrigger === t.id ? '1px solid #111' : '1px solid #e8e8e6', background: cTrigger === t.id ? '#fafaf9' : '#fff' }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#0a0a0a' }}>{t.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {cStep === 1 && (
                  <div>
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a', marginBottom: 6 }}>Nombre del agente</div>
                      <input className="sett-input" placeholder="Ej: Bot Confirmación COD" value={cName} onChange={e => setCName(e.target.value)} />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a', marginBottom: 6 }}>Descripción (opcional)</div>
                      <input className="sett-input" placeholder="¿Qué hace este agente?" value={cDesc} onChange={e => setCDesc(e.target.value)} />
                    </div>
                  </div>
                )}

                {cStep === 2 && isNewTenant && !cTemplate && (
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#0a0a0a', marginBottom: 4 }}>Elige una plantilla</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 14 }}>Después completas los datos de tu negocio</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {STARTER_TEMPLATES.map(t => (
                        <div key={t.id} onClick={() => { setCTemplate(t.id); setCPrompt(t.prompt); }}
                          style={{ padding: '14px 18px', borderRadius: 8, cursor: 'pointer', transition: 'all .15s', border: '1px solid #e8e8e6', background: '#fff' }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#0a0a0a' }}>{t.label}</div>
                          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{t.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {cStep === 2 && (!isNewTenant || cTemplate) && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a' }}>Instrucciones del agente</div>
                      {isNewTenant && <button className="sett-btn sett-btn-ghost" style={{ height: 28, fontSize: 12 }} onClick={() => { setCTemplate(''); setCPrompt(''); }}>Cambiar plantilla</button>}
                    </div>
                    {isNewTenant
                      ? <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>Edita el texto libremente; solo completa lo que está entre [[ ]]</div>
                      : <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>Usa {'{{tienda}}'} para insertar el nombre de la tienda</div>}
                    <textarea className="sett-input" value={cPrompt} onChange={e => setCPrompt(e.target.value)} style={{ height: 'auto', minHeight: 240, resize: 'vertical', padding: 14, lineHeight: 1.5, fontSize: 13 }} />
                    {isNewTenant && pendingFields(cPrompt).length > 0 && (
                      <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fde68a', fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>Falta completar ({pendingFields(cPrompt).length}):</div>
                        {pendingFields(cPrompt).join(' · ')}
                      </div>
                    )}
                  </div>
                )}

                {cStep === 3 && (
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#0a0a0a', marginBottom: 14 }}>¿Qué puede hacer este agente?</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                      {toolOpts.map(t => (
                        <div key={t.id} onClick={() => setCTools(prev => prev.includes(t.id) ? prev.filter(x=>x!==t.id) : [...prev, t.id])}
                          style={{ padding: '12px 16px', borderRadius: 8, cursor: 'pointer', transition: 'all .15s', border: cTools.includes(t.id) ? '1px solid #111' : '1px solid #e8e8e6', background: cTools.includes(t.id) ? '#fafaf9' : '#fff', display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, border: cTools.includes(t.id) ? '1px solid #111' : '1px solid #c8c8c8', background: cTools.includes(t.id) ? '#0a0a0a' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {cTools.includes(t.id) && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0a0a0a' }}>{t.label}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af' }}>{t.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a', marginBottom: 8 }}>Modelo</div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div onClick={() => setCModel('')} style={{ flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer', textAlign: 'center', border: !cModel ? '1px solid #111' : '1px solid #e8e8e6', fontSize: 13, fontWeight: 600 }}>Predeterminado</div>
                      {providerModels.map(m => (
                        <div key={m.id} onClick={() => setCModel(m.id)} style={{ flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer', textAlign: 'center', border: cModel === m.id ? '1px solid #111' : '1px solid #e8e8e6', fontSize: 13, fontWeight: 600 }}>{m.label}</div>
                      ))}
                    </div>
                  </div>
                )}

                {cStep === 4 && (
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#0a0a0a', marginBottom: 16 }}>Resumen</div>
                    <div style={{ background: '#f5f5f3', borderRadius: 8, padding: 20 }}>
                      {[{ l:'Nombre', v:cName },{ l:'Tipo', v:triggerOpts.find(t=>t.id===cTrigger)?.label },{ l:'Proveedor', v:prov.name },{ l:'Herramientas', v:cTools.map(t=>toolOpts.find(x=>x.id===t)?.label||t).join(', ')||'Ninguna' },{ l:'Modelo', v:cModel?providerModels.find(m=>m.id===cModel)?.label:'Predeterminado' }].map(r => (
                        <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e8e8e6' }}>
                          <span style={{ fontSize: 13, color: '#9ca3af' }}>{r.l}</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#0a0a0a' }}>{r.v}</span>
                        </div>
                      ))}
                      <div style={{ marginTop: 14, fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>Prompt</div>
                      <div style={{ fontSize: 12, color: '#555', background: '#fff', padding: 12, borderRadius: 8, maxHeight: 100, overflow: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                        {cPrompt.slice(0, 300)}{cPrompt.length > 300 ? '...' : ''}
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                  <button className="sett-btn sett-btn-ghost" onClick={() => cStep === 0 ? resetCreate() : setCStep(s => s - 1)}>{cStep === 0 ? 'Cancelar' : 'Atrás'}</button>
                  {cStep < 4
                    ? <button className="sett-btn sett-btn-primary" disabled={(cStep===0&&!cTrigger)||(cStep===1&&!cName.trim())||(cStep===2&&(!cPrompt.trim()||(isNewTenant&&pendingFields(cPrompt).length>0)))} onClick={() => setCStep(s=>s+1)}>Siguiente</button>
                    : <button className="sett-btn sett-btn-primary" onClick={createAgent}>Crear agente</button>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL: EDITAR */}
        {editAgent && (
          <div className="sett-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setEditAgent(null); }}>
            <div className="sett-modal ai-modal" style={{ width: 580, maxHeight: '90vh', overflow: 'auto' }}>
              <div style={{ padding: '24px 28px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>Editar agente</div>
                <button className="sett-btn sett-btn-ghost" style={{ width: 30, height: 30, padding: 0, borderRadius: 6, justifyContent: 'center' }} onClick={() => setEditAgent(null)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div style={{ padding: '20px 28px 28px' }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a', marginBottom: 6 }}>Nombre</div>
                  <input className="sett-input" value={editAgent.name} onChange={e => setEditAgent({...editAgent, name: e.target.value})} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a', marginBottom: 6 }}>Descripción</div>
                  <input className="sett-input" value={editAgent.description} onChange={e => setEditAgent({...editAgent, description: e.target.value})} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a', marginBottom: 6 }}>¿Cuándo responde?</div>
                  <select className="sett-select" style={{ width: '100%' }} value={editAgent.triggerType} onChange={e => setEditAgent({...editAgent, triggerType: e.target.value})}>
                    {triggerOpts.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a', marginBottom: 6 }}>Instrucciones del agente</div>
                  <textarea className="sett-input" value={editAgent.systemPrompt} onChange={e => setEditAgent({...editAgent, systemPrompt: e.target.value})} style={{ height: 'auto', minHeight: 200, resize: 'vertical', padding: 14, lineHeight: 1.5, fontSize: 13 }} />
                  {isNewTenant && pendingFields(editAgent.systemPrompt).length > 0 && (
                    <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fde68a', fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Falta completar ({pendingFields(editAgent.systemPrompt).length}):</div>
                      {pendingFields(editAgent.systemPrompt).join(' · ')}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a', marginBottom: 8 }}>Herramientas</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {toolOpts.map(t => (
                      <span key={t.id} onClick={() => { const cur=editAgent.toolsEnabled||[]; setEditAgent({...editAgent, toolsEnabled: cur.includes(t.id)?cur.filter(x=>x!==t.id):[...cur,t.id]}); }}
                        className="sett-tag" style={{ cursor: 'pointer', background: (editAgent.toolsEnabled||[]).includes(t.id)?'#0a0a0a':'#f0f0ee', color: (editAgent.toolsEnabled||[]).includes(t.id)?'#fff':'#374151' }}>{t.label}</span>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button className="sett-btn sett-btn-ghost" onClick={() => setEditAgent(null)}>Cancelar</button>
                  <button className="sett-btn sett-btn-primary" disabled={isNewTenant && pendingFields(editAgent.systemPrompt).length > 0} onClick={saveEdit}>Guardar cambios</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL: PLAYGROUND */}
        {playAgent && (
          <div className="sett-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setPlayAgent(null); }}>
            <div className="sett-modal ai-modal" style={{ width: 560, maxHeight: '90vh', overflow: 'auto' }}>
              <div style={{ padding: '24px 28px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>Probar: {playAgent.name}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{playAgent.triggerLabel}</div>
                </div>
                <button className="sett-btn sett-btn-ghost" style={{ width: 30, height: 30, padding: 0, borderRadius: 6, justifyContent: 'center' }} onClick={() => setPlayAgent(null)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div style={{ padding: '20px 28px 28px' }}>
                <div style={{ background: '#f5f5f3', borderRadius: 8, padding: 20, minHeight: 180, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {playChat.length === 0 && !playLoading && <div style={{ textAlign: 'center', color: '#c4c4c2', fontSize: 13, padding: '50px 0' }}>Escribe un mensaje para probar cómo responde<br/>Responde igual que con tus clientes, pero no crea pedidos ni envía mensajes</div>}
                  {playChat.map((m, i) => m.role === 'user' ? (
                    <div key={i} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <div style={{ padding: '10px 16px', borderRadius: '8px 8px 4px 8px', background: '#0a0a0a', color: '#fff', fontSize: 13, maxWidth: '80%', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{m.content}</div>
                    </div>
                  ) : (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                      <div style={{ padding: '10px 16px', borderRadius: '8px 8px 8px 4px', background: '#fff', color: m.error ? '#b91c1c' : '#0a0a0a', fontSize: 13, maxWidth: '80%', lineHeight: 1.5, border: '1px solid #e8e8e6', whiteSpace: 'pre-wrap' }}>{m.content}</div>
                      {m.action && <div style={{ fontSize: 11.5, color: '#6b7280' }}>En una conversación real aquí usaría: {m.action} (en la prueba no se ejecuta)</div>}
                    </div>
                  ))}
                  {playLoading && <div style={{ display: 'flex' }}><div style={{ padding: '10px 16px', borderRadius: '8px 8px 8px 4px', background: '#fff', color: '#9ca3af', fontSize: 13, border: '1px solid #e8e8e6' }}>Pensando...</div></div>}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <input className="sett-input" placeholder="Escribe un mensaje de prueba..." value={playMsg} onChange={e => setPlayMsg(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !playLoading) testAgentChat(playAgent.id); }} />
                  <button className="sett-btn sett-btn-primary" style={{ height: 44, padding: '0 16px' }} onClick={() => testAgentChat(playAgent.id)} disabled={playLoading || !playMsg.trim()}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      {/* MODAL: CREAR/EDITAR DOCUMENTO */}
      {kbEdit && (
        <div className="sett-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setKbEdit(null); }}>
          <div className="sett-modal ai-modal" style={{ width: 620, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '24px 28px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>{kbEdit.id ? 'Editar documento' : 'Nuevo documento'}</div>
              <button className="sett-btn sett-btn-ghost" style={{ width: 30, height: 30, padding: 0, borderRadius: 6, justifyContent: 'center' }} onClick={() => setKbEdit(null)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding: '20px 28px 28px' }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a', marginBottom: 6 }}>Título</div>
                <input className="sett-input" placeholder="Ej: Política de garantías" value={kbEdit.title} onChange={e => setKbEdit({ ...kbEdit, title: e.target.value })} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a', marginBottom: 6 }}>Categoría</div>
                <select className="sett-select" style={{ width: '100%' }} value={kbEdit.category} onChange={e => setKbEdit({ ...kbEdit, category: e.target.value })}>
                  <option value="general">General</option>
                  <option value="garantias">Garantías</option>
                  <option value="envios">Envíos</option>
                  <option value="productos">Productos</option>
                  <option value="pagos">Pagos</option>
                  <option value="faq">Preguntas frecuentes</option>
                </select>
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a' }}>Contenido</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{kbEdit.content.length} caracteres</div>
                </div>
                <textarea className="sett-input" placeholder="Escribe aquí la información que el agente debe conocer..." value={kbEdit.content} onChange={e => setKbEdit({ ...kbEdit, content: e.target.value })} style={{ height: 'auto', minHeight: 280, resize: 'vertical', padding: 14, lineHeight: 1.6, fontSize: 13 }} />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="sett-btn sett-btn-ghost" onClick={() => setKbEdit(null)}>Cancelar</button>
                <button className="sett-btn sett-btn-primary" onClick={saveKbDoc} disabled={kbSaving || !kbEdit.title.trim() || !kbEdit.content.trim()}>{kbSaving ? 'Guardando...' : kbEdit.id ? 'Guardar cambios' : 'Crear documento'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Si el panel falla, mostrar el error en vez de dejar la página en blanco
class AILabBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error('AILab error', err, info?.componentStack); }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 40, fontFamily: 'inherit' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>No se pudo mostrar Inteligencia artificial</div>
          <div style={{ fontSize: 13, color: '#666', marginTop: 6 }}>Envíale este mensaje a soporte:</div>
          <pre style={{ marginTop: 10, padding: 12, background: '#fafafa', border: '1px solid #e5e5e5', borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap', color: '#b42318' }}>{String(this.state.err && (this.state.err.stack || this.state.err.message || this.state.err)).slice(0, 900)}</pre>
          <button onClick={() => this.setState({ err: null })} style={{ marginTop: 10, height: 34, padding: '0 14px', border: '1px solid #dcdcdc', borderRadius: 6, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>Reintentar</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AILab(props) {
  return <AILabBoundary><AILabInner {...props} /></AILabBoundary>;
}
